// Pocket ID OIDC, button-driven. No edge intercept. Laid out like Proton's
// sign-in (account/public Layout): radial page background, the Layer7 lockup
// top-left, and a centred sign-in card.
import { useState } from "react";
import { KeyRound, Fingerprint } from "lucide-react";
import { AuthApi } from "@/api";
import { Button } from "@/proton/ui/Button";
import { Icon } from "@/proton/ui/Icon";
import { PublicLayout } from "@/proton/ui/Public";

type Stage = "idle" | "passkey" | "code" | "locked" | "expired";

export function LoginPage() {
  const [stage, setStage] = useState<Stage>("idle");

  function beginSso() {
    setStage("passkey");
    AuthApi.beginLogin(new URLSearchParams(window.location.search).get("return_to") ?? "/home");
  }

  return (
    <PublicLayout footer="Layer7 SSO via Pocket ID · workspace.l7.gg">
      <div className="sign-layout">
        {stage === "idle" && (
          <>
            <SignHeader title="Sign in" subtitle="to continue to Layer7 Workspace" />
            <Button color="norm" size="large" fullWidth onClick={beginSso}>
              <Icon as={KeyRound} /> Continue with Layer7 SSO
            </Button>
            <Button shape="ghost" color="norm" fullWidth className="mt-3" onClick={() => setStage("code")}>
              Lost your device? Use a recovery code
            </Button>
            <p className="mt-6 mb-0 text-sm color-weak text-center">
              Internal tool for the Layer7 team.
            </p>
          </>
        )}
        {stage === "passkey" && (
          <>
            <SignHeader title="Authenticating" subtitle="Approve the passkey prompt on this device." />
            <div className="flex flex-col items-center gap-3 py-6">
              <span className="sign-layout-passkey"><Icon as={Fingerprint} size={8} /></span>
              <span className="text-sm color-weak">Waiting for your passkey (Pocket ID)</span>
            </div>
            <Button fullWidth onClick={() => setStage("idle")}>Cancel</Button>
          </>
        )}
        {stage === "code" && <RecoveryCodeForm onBack={() => setStage("idle")} />}
        {stage === "locked" && <LockedState onBack={() => setStage("idle")} />}
        {stage === "expired" && <ExpiredState onResume={() => setStage("passkey")} />}
      </div>
    </PublicLayout>
  );
}

function SignHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-6">
      <h1 className="sign-layout-title">{title}</h1>
      {subtitle && <div className="mt-2 color-weak">{subtitle}</div>}
    </div>
  );
}

function RecoveryCodeForm({ onBack }: { onBack: () => void }) {
  return (
    <>
      <SignHeader title="Enter recovery code" subtitle="Six-digit code from your admin-issued recovery sheet." />
      <div className="flex gap-2 justify-center mb-6">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <input key={i} maxLength={1} aria-label={`Digit ${i + 1}`} className="field-input sign-layout-digit" />
        ))}
      </div>
      <Button fullWidth onClick={onBack}>Back</Button>
    </>
  );
}
function LockedState({ onBack }: { onBack: () => void }) {
  return (
    <>
      <SignHeader title="Account locked" subtitle="Too many failed attempts. Reach out to your Layer7 admin, or wait 15 minutes." />
      <Button fullWidth onClick={onBack}>Back to sign-in</Button>
    </>
  );
}
function ExpiredState({ onResume }: { onResume: () => void }) {
  return (
    <>
      <SignHeader title="Session expired" subtitle="Your session has been out for 12 hours. Sign in again to continue." />
      <Button color="norm" size="large" fullWidth onClick={onResume}>Sign in again</Button>
    </>
  );
}
